import { useState, useMemo } from 'react';

import { useWorkspaceMemberSuggestions } from '@/hooks';
import { resolveMemberLabel } from '@/utils';

import type {
  OperationHistoryEvent,
  OperationHistoryFilters,
} from '../OperationHistory.types';

type FilterUpdater =
  | OperationHistoryFilters
  | ((prev: OperationHistoryFilters) => OperationHistoryFilters);

export interface UseOperationHistoryFilterResult<
  TEvent extends OperationHistoryEvent,
> {
  filters: OperationHistoryFilters;
  filteredEvents: TEvent[];
  setFilters: (updater: FilterUpdater) => void;
  updateFilter: (key: keyof OperationHistoryFilters, value: unknown) => void;
  clearFilters: () => void;
  selectedAuthorLabels: string[];
  availableEventTypes: string[];
  hasActiveFilters: boolean;
}

const DEFAULT_FILTERS: OperationHistoryFilters = {
  search: '',
  eventTypes: [],
  dateFrom: null,
  dateTo: null,
  authorIds: [],
};

function matchesSearchText(
  event: OperationHistoryEvent,
  searchText: string
): boolean {
  const matchesDescription = event.description
    .toLowerCase()
    .includes(searchText);
  const matchesAuthor = event.authorName.toLowerCase().includes(searchText);
  const matchesEventType = event.eventType
    .toLowerCase()
    .replace('_', ' ')
    .includes(searchText);

  const matchesMetadata =
    event.metadata &&
    Object.values(event.metadata).some(
      (value) =>
        value != null && String(value).toLowerCase().includes(searchText)
    );

  return (
    matchesDescription || matchesAuthor || matchesEventType || !!matchesMetadata
  );
}

function matchesDateRange(
  event: OperationHistoryEvent,
  dateFrom: Date | null,
  dateTo: Date | null
): boolean {
  const eventDate = new Date(event.createdAt);

  if (dateFrom && eventDate < dateFrom) {
    return false;
  }

  if (dateTo) {
    const endOfDay = new Date(dateTo);
    endOfDay.setHours(23, 59, 59, 999);
    if (eventDate > endOfDay) {
      return false;
    }
  }

  return true;
}

export function useOperationHistoryFilter<TEvent extends OperationHistoryEvent>(
  events: TEvent[]
): UseOperationHistoryFilterResult<TEvent> {
  const [filters, setFilters] =
    useState<OperationHistoryFilters>(DEFAULT_FILTERS);

  const { members } = useWorkspaceMemberSuggestions();

  const availableEventTypes = useMemo(() => {
    const eventTypes = new Set(events.map((event) => event.eventType));
    return Array.from(eventTypes).sort();
  }, [events]);

  const filteredEvents = useMemo(() => {
    return events.filter((event) => {
      if (filters.search) {
        const searchText = filters.search.toLowerCase();
        if (!matchesSearchText(event, searchText)) {
          return false;
        }
      }

      if (
        filters.eventTypes.length > 0 &&
        !filters.eventTypes.includes(event.eventType)
      ) {
        return false;
      }

      if (!matchesDateRange(event, filters.dateFrom, filters.dateTo)) {
        return false;
      }

      if (
        filters.authorIds.length > 0 &&
        (event.authorId === undefined ||
          !filters.authorIds.includes(event.authorId))
      ) {
        return false;
      }

      return true;
    });
  }, [events, filters]);

  const selectedAuthorLabels = useMemo(
    () =>
      filters.authorIds.map((userId) => resolveMemberLabel(members, userId)),
    [filters.authorIds, members]
  );

  const updateFilter = (
    key: keyof OperationHistoryFilters,
    value: unknown
  ): void => {
    setFilters((previous) => ({
      ...previous,
      [key]: value,
    }));
  };

  const clearFilters = (): void => {
    setFilters(DEFAULT_FILTERS);
  };

  const hasActiveFilters = useMemo(() => {
    return (
      filters.search !== '' ||
      filters.eventTypes.length > 0 ||
      filters.dateFrom !== null ||
      filters.dateTo !== null ||
      filters.authorIds.length > 0
    );
  }, [filters]);

  return {
    filters,
    filteredEvents,
    setFilters,
    updateFilter,
    clearFilters,
    selectedAuthorLabels,
    availableEventTypes,
    hasActiveFilters,
  };
}
